import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './auth/store';
import { fetchMe, ApiError } from './api/client';
import { RequireAuth } from './components/RequireAuth';
import { AppShell } from './components/AppShell';
import { Toasts } from './components/Toasts';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SettingsPage } from './pages/SettingsPage';
import { ForcePasswordChangePage } from './pages/ForcePasswordChangePage';
// Directory (clients, principals, sites)
import { DirectoryIndividualsPage } from './pages/DirectoryIndividualsPage';
import { DirectoryBusinessesPage } from './pages/DirectoryBusinessesPage';
import { OrganizationDetailPage } from './pages/OrganizationDetailPage';
import { SitesPage } from './pages/SitesPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { PrincipalDetailPage } from './pages/PrincipalDetailPage';
// People
import { PersonnelPage } from './pages/PersonnelPage';
import { PersonnelDetailPage } from './pages/PersonnelDetailPage';
import { CredentialExpiryPage } from './pages/CredentialExpiryPage';
import { PersonsPage } from './pages/PersonsPage';
import { PersonDetailPage } from './pages/PersonDetailPage';
// My Workspace (self-service)
import { MyProfilePage } from './pages/MyProfilePage';
import { MySchedulePage } from './pages/MySchedulePage';
import { MyTimesheetsPage } from './pages/MyTimesheetsPage';
import { MyCredentialsPage } from './pages/MyCredentialsPage';
import { WebmailPage } from './pages/WebmailPage';
// Applicant portal
import { PortalApplicationPage } from './pages/PortalApplicationPage';
import { PortalUsersPage } from './pages/PortalUsersPage';
// DSR Admin
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminUserDetailPage } from './pages/AdminUserDetailPage';
import { AdminRolesPage } from './pages/AdminRolesPage';
// Compliance
import { ComplianceDashboardPage } from './pages/ComplianceDashboardPage';
import { GapAnalysisPage } from './pages/GapAnalysisPage';
import { CompanyLicensesPage } from './pages/CompanyLicensesPage';
import { CompanyLicenseDetailPage } from './pages/CompanyLicenseDetailPage';
import { InsuranceBondsPage } from './pages/InsuranceBondsPage';
import { RequirementsCatalogPage } from './pages/RequirementsCatalogPage';
import { RosterCompliancePage } from './pages/RosterCompliancePage';
// Operations
import { SchedulePage } from './pages/SchedulePage';
import { TimesheetsPage } from './pages/TimesheetsPage';
import { IncidentsPage } from './pages/IncidentsPage';
import { IncidentDetailPage } from './pages/IncidentDetailPage';
import { IncidentReviewQueuePage } from './pages/IncidentReviewQueuePage';
import { DARsPage } from './pages/DARsPage';
import { DARDetailPage } from './pages/DARDetailPage';
import { VisitorsPage } from './pages/VisitorsPage';
// Assets
import { AssetsPage } from './pages/AssetsPage';
import { AssetDetailPage } from './pages/AssetDetailPage';
// EP
import { DetailsPage } from './pages/DetailsPage';
import { DetailPlannerPage } from './pages/DetailPlannerPage';
import { SurveysPage } from './pages/SurveysPage';
import { SurveyDetailPage } from './pages/SurveyDetailPage';
// Documents & Templates
import { DocumentsPage } from './pages/DocumentsPage';
import { ReportTemplatesPage } from './pages/ReportTemplatesPage';
// Billing
import { ContractsPage } from './pages/ContractsPage';
import { BillingRatesPage } from './pages/BillingRatesPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
// Financials
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { VendorsPage } from './pages/VendorsPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { IncomePage } from './pages/IncomePage';
import { RetainersPage } from './pages/RetainersPage';
import { ContractorPaymentsPage } from './pages/ContractorPaymentsPage';
import { FinancialReportsPage } from './pages/FinancialReportsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, err) => {
        // Don't retry auth failures or client errors.
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// Redirect a superseded list route to its Directory replacement while carrying
// the query string across (e.g. /principals?organization_id=5 →
// /directory/individuals?organization_id=5).
function RedirectPreservingQuery({ to }: { to: string }): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

export function App(): JSX.Element {
  const setMe = useAuthStore((s) => s.setMe);
  const setLoading = useAuthStore((s) => s.setLoading);

  // Bootstrap: fetch /me on app load. The endpoint always returns 200,
  // performing an inline refresh-cookie rotation if a session exists
  // (eliminates the 401 console noise on cold load). `me.user === null`
  // means no session — we drop to /login. Otherwise we hydrate the
  // auth store with the full session payload.
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    fetchMe(ac.signal)
      .then(({ user, ...rest }) => setMe(user ? { user, ...rest } : null))
      .catch(() => setMe(null));
    return () => ac.abort();
  }, [setMe, setLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/app" element={<DashboardPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/force-password-change" element={<ForcePasswordChangePage />} />

            {/* Directory */}
            <Route path="/directory/individuals" element={<DirectoryIndividualsPage />} />
            <Route path="/directory/businesses" element={<DirectoryBusinessesPage />} />
            {/* Superseded list routes → redirect (keeps bookmarks/deep links working) */}
            <Route
              path="/organizations"
              element={<RedirectPreservingQuery to="/directory/businesses" />}
            />
            <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/sites/:id" element={<SiteDetailPage />} />
            <Route
              path="/principals"
              element={<RedirectPreservingQuery to="/directory/individuals" />}
            />
            <Route path="/principals/:id" element={<PrincipalDetailPage />} />

            {/* People */}
            <Route path="/persons" element={<PersonsPage />} />
            <Route path="/persons/:id" element={<PersonDetailPage />} />
            <Route path="/personnel" element={<PersonnelPage />} />
            <Route path="/personnel/:id" element={<PersonnelDetailPage />} />
            <Route path="/credentials/expiring" element={<CredentialExpiryPage />} />

            {/* My Workspace (self-service) */}
            <Route path="/me/profile" element={<MyProfilePage />} />
            <Route path="/me/schedule" element={<MySchedulePage />} />
            <Route path="/me/timesheets" element={<MyTimesheetsPage />} />
            <Route path="/me/credentials" element={<MyCredentialsPage />} />
            <Route path="/me/webmail" element={<WebmailPage />} />

            {/* Applicant portal */}
            <Route path="/portal/application" element={<PortalApplicationPage />} />
            <Route path="/orgs/:id/portal-users" element={<PortalUsersPage />} />

            {/* DSR Admin */}
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
            <Route path="/admin/roles" element={<AdminRolesPage />} />

            {/* Compliance */}
            <Route path="/compliance" element={<ComplianceDashboardPage />} />
            <Route path="/compliance/gap" element={<GapAnalysisPage />} />
            <Route path="/compliance/licenses" element={<CompanyLicensesPage />} />
            <Route path="/compliance/licenses/:id" element={<CompanyLicenseDetailPage />} />
            <Route path="/compliance/insurance" element={<InsuranceBondsPage />} />
            <Route path="/compliance/catalog" element={<RequirementsCatalogPage />} />
            <Route path="/compliance/roster" element={<RosterCompliancePage />} />

            {/* Operations */}
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/incidents/review" element={<IncidentReviewQueuePage />} />
            <Route path="/incidents/:id" element={<IncidentDetailPage />} />
            <Route path="/dars" element={<DARsPage />} />
            <Route path="/dars/:id" element={<DARDetailPage />} />
            <Route path="/visitors" element={<VisitorsPage />} />

            {/* Assets */}
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/assets/:id" element={<AssetDetailPage />} />

            {/* Executive Protection */}
            <Route path="/details" element={<DetailsPage />} />
            <Route path="/details/:id" element={<DetailPlannerPage />} />
            <Route path="/surveys" element={<SurveysPage />} />
            <Route path="/surveys/:id" element={<SurveyDetailPage />} />

            {/* Documents & Templates */}
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/report-templates" element={<ReportTemplatesPage />} />

            {/* Billing */}
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/billing-rates" element={<BillingRatesPage />} />
            <Route path="/app/invoices" element={<InvoicesPage />} />
            <Route path="/app/invoices/:id" element={<InvoiceDetailPage />} />

            {/* Financials */}
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/income" element={<IncomePage />} />
            <Route path="/retainers" element={<RetainersPage />} />
            <Route path="/contractor-payments" element={<ContractorPaymentsPage />} />
            <Route path="/financial-reports" element={<FinancialReportsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </BrowserRouter>
      <Toasts />
    </QueryClientProvider>
  );
}
